// NEGF Transport in a project: 1 the SK model (from an SKFitter fit or a model file), 2 the device (cell, transport
// direction, length, width; drawn as it is set up), 3 the calculation (T(E), I-V, LDOS) and its results.
// The device builder follows the usual layout of QuantumATK-like tools: the structure view next to its settings.
import { Alert, Card, Loader, Stack, Stepper } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { patch, type ProjectDetail } from "../../lib/api";
import { useMe } from "../../lib/hooks";
import { defaultMemory, type NegfMemory } from "../../lib/negf";
import { CalcStep } from "./CalcStep";
import { ContactsStep } from "./ContactsStep";
import { DeviceStep } from "./DeviceStep";
import { GatesStep } from "./GatesStep";
import { ModelStep } from "./ModelStep";
import { NegfExampleBanner } from "../NegfExamples";

export function NegfWorkspace({ project }: { project: ProjectDetail }) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const saved = project.settings?.negf as Partial<NegfMemory> | undefined;
  const [mem, setMem] = useState<NegfMemory>(() => ({ ...defaultMemory(), ...(saved ?? {}) }));
  const [step, setStep] = useState<number>(saved?.model_file ? (saved.jobs && Object.keys(saved.jobs).length ? 4 : 1) : 0);

  // the workspace's state is remembered in the project (as SKFitter does)
  const latest = useRef(mem);
  latest.current = mem;
  const remember = useCallback((next: Partial<NegfMemory>) => {
    const merged = { ...latest.current, ...next };
    latest.current = merged;
    setMem(merged);
    patch(`/projects/${project.id}`, { settings: { ...(project.settings ?? {}), negf: merged } })
      .then(() => qc.invalidateQueries({ queryKey: ["project", project.id] }))
      .catch(() => undefined);
  }, [project.id, project.settings, qc]);
  const update = useCallback((next: Partial<NegfMemory>) => setMem((m) => {
    const merged = { ...m, ...next };
    latest.current = merged;
    return merged;
  }), []);

  if (!me) return <Card withBorder><Loader size="sm" /></Card>;
  const limits = project.limits ?? me.limits;
  if (!limits["negf.enabled"])
    return <Alert color="orange" title="Not included in your plan">NEGF Transport is not part of your plan.</Alert>;

  const s = mem.summary;
  return (
    <Stack gap="md">
    <NegfExampleBanner project={project} mem={mem} />
    <Card withBorder>
      <Stepper active={step} onStepClick={setStep} size="sm" allowNextStepsSelect={false}>
        <Stepper.Step label="Model" description={s ? `${s.formula} · ${s.orbitals_per_cell} orbitals / cell` : "from a Slater-Koster fit"}>
          <ModelStep project={project} mem={mem} remember={remember} update={update} onNext={() => setStep(1)} limits={limits} />
        </Stepper.Step>
        <Stepper.Step label="Device" description="cell, transport direction, size" disabled={!mem.model_file}>
          {mem.model_file && <DeviceStep project={project} mem={mem} update={update} remember={remember} limits={limits}
            onBack={() => setStep(0)} onNext={() => setStep(2)} />}
        </Stepper.Step>
        <Stepper.Step label="Gates" description="electrostatics (self-consistent)" disabled={!mem.model_file}>
          {mem.model_file && <GatesStep project={project} mem={mem} update={update} remember={remember} limits={limits}
            onBack={() => setStep(1)} onNext={() => setStep(3)} />}
        </Stepper.Step>
        <Stepper.Step label="Contacts & fields" description="superconductors, B, g-tensor" disabled={!mem.model_file}>
          {mem.model_file && <ContactsStep project={project} mem={mem} update={update} remember={remember} limits={limits}
            onBack={() => setStep(2)} onNext={() => setStep(4)} />}
        </Stepper.Step>
        <Stepper.Step label="Calculation" description="T(E), I–V, LDOS, self-consistent, fields, superconducting" disabled={!mem.model_file}>
          {mem.model_file && <CalcStep project={project} mem={mem} update={update} remember={remember} limits={limits}
            onBack={() => setStep(3)} />}
        </Stepper.Step>
      </Stepper>
    </Card>
    </Stack>
  );
}
